import { Alert, Box, Button, Card, CardContent, Link, Skeleton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { SubscriptionInvoice } from '@/entities/subscription';
import { useBillingHistory } from '../api/useBillingHistory';
import { formatAmount } from '../model/formatAmount';
import { formatSubscriptionDate } from '../../model/formatSubscriptionDate';

type Props = {
  emptyState: 'free' | 'paid';
};

const GRID_COLUMNS = '120px 1fr 100px 80px';

export const BillingHistoryTable = ({ emptyState }: Props) => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError, refetch } = useBillingHistory();

  if (isLoading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Skeleton variant="text" width="40%" height={32} />
            <Skeleton variant="rectangular" height={48} />
            <Skeleton variant="rectangular" height={48} />
            <Skeleton variant="rectangular" height={48} />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    const handleRetry = () => {
      void refetch();
    };
    return (
      <Card variant="outlined">
        <CardContent>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                {t('subscription.billingHistory.retry')}
              </Button>
            }
          >
            {t('subscription.billingHistory.errorLoading')}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const invoices: SubscriptionInvoice[] = data?.invoices ?? [];

  const emptyCopyKey =
    emptyState === 'free' ? 'subscription.billingHistory.emptyFree' : 'subscription.billingHistory.emptyPaid';

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6">{t('subscription.billingHistory.title')}</Typography>

          {invoices.length === 0 ? (
            <Box
              sx={{
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                py: 6,
                px: 3,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t(emptyCopyKey)}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0} divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: GRID_COLUMNS,
                  columnGap: 2,
                  py: 1,
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {t('subscription.billingHistory.columnDate')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('subscription.billingHistory.columnDescription')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                  {t('subscription.billingHistory.columnAmount')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('subscription.billingHistory.columnPdf')}
                </Typography>
              </Box>

              {invoices.map((invoice) => (
                <Box
                  key={invoice.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: GRID_COLUMNS,
                    columnGap: 2,
                    py: 1,
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body2">{formatSubscriptionDate(invoice.createdAt, i18n.language)}</Typography>
                  <Typography variant="body2">{invoice.description}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'right' }}>
                    {formatAmount(invoice.amountCents, invoice.currency, i18n.language)}
                  </Typography>
                  {invoice.invoicePdfUrl !== null ? (
                    <Link
                      href={invoice.invoicePdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      variant="body2"
                    >
                      {t('subscription.billingHistory.downloadPdf')}
                    </Link>
                  ) : (
                    <Box />
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
